import React, { FC, useState, useCallback, useEffect } from "react";
import {
    Button,
    OnboardingAside,
    OnboardingContent,
    FieldCheckbox,
    FieldDropdown,
    Accordion,
    LoadingSpinner,
} from "@xapp/components";

import { ClientApp } from "models/App";
import { CrmDayOfWeek } from "generated/graphql";

export interface AvailabilityStepProps {
    /**
     * Optional style applied to the root div
     */
    style?: React.CSSProperties;
    /**
     * Optional class name to apply to the root div
     */
    className?: string;

    aside?: React.ReactNode;

    app?: Pick<ClientApp, "availability" | "appId" | "organizationId">;
    /**
     * Callback when user clicks continue
     */
    onNext?: (availability: Pick<ClientApp, "availability" | "appId" | "organizationId">) => void;
    /**
     * Callback when user clicks back
     */
    onBack?: () => void;

    /**
     * Loading state during initial query of information
     */
    loading?: boolean;
    /**
     * Saving state when submitting form data
     */
    saving?: boolean;
}

const DAYS_OF_WEEK = [
    { value: CrmDayOfWeek.Monday, label: "Monday" },
    { value: CrmDayOfWeek.Tuesday, label: "Tuesday" },
    { value: CrmDayOfWeek.Wednesday, label: "Wednesday" },
    { value: CrmDayOfWeek.Thursday, label: "Thursday" },
    { value: CrmDayOfWeek.Friday, label: "Friday" },
    { value: CrmDayOfWeek.Saturday, label: "Saturday" },
    { value: CrmDayOfWeek.Sunday, label: "Sunday" },
];

const DAY_PRESET_OPTIONS = [
    {
        value: "weekdays",
        label: "Monday - Friday",
        days: [
            CrmDayOfWeek.Monday,
            CrmDayOfWeek.Tuesday,
            CrmDayOfWeek.Wednesday,
            CrmDayOfWeek.Thursday,
            CrmDayOfWeek.Friday,
        ],
    },
    {
        value: "seven_days",
        label: "7 Days a Week",
        days: [
            CrmDayOfWeek.Monday,
            CrmDayOfWeek.Tuesday,
            CrmDayOfWeek.Wednesday,
            CrmDayOfWeek.Thursday,
            CrmDayOfWeek.Friday,
            CrmDayOfWeek.Saturday,
            CrmDayOfWeek.Sunday,
        ],
    },
    {
        value: "weekdays_saturday",
        label: "Monday - Friday + Saturday",
        days: [
            CrmDayOfWeek.Monday,
            CrmDayOfWeek.Tuesday,
            CrmDayOfWeek.Wednesday,
            CrmDayOfWeek.Thursday,
            CrmDayOfWeek.Friday,
            CrmDayOfWeek.Saturday,
        ],
    },
    {
        value: "custom",
        label: "Let me select my own",
        days: [],
    },
];

const TIME_OPTIONS = [
    { value: "09:00", text: "9:00 AM" },
    { value: "10:00", text: "10:00 AM" },
    { value: "11:00", text: "11:00 AM" },
    { value: "12:00", text: "12:00 PM" },
    { value: "13:00", text: "1:00 PM" },
    { value: "14:00", text: "2:00 PM" },
    { value: "15:00", text: "3:00 PM" },
    { value: "16:00", text: "4:00 PM" },
    { value: "17:00", text: "5:00 PM" },
    { value: "18:00", text: "6:00 PM" },
];

const ADVANCE_BOOKING_OPTIONS = [
    { value: 1, text: "1 day" },
    { value: 2, text: "2 days" },
    { value: 3, text: "3 days" },
    { value: 7, text: "1 week" },
    { value: 14, text: "2 weeks" },
    { value: 30, text: "1 month" },
];

export const AvailabilityStep: FC<AvailabilityStepProps> = (props) => {
    const { aside, app, onNext, onBack, loading, saving } = props;

    const [serviceDays, setServiceDays] = useState<CrmDayOfWeek[]>([
        CrmDayOfWeek.Monday,
        CrmDayOfWeek.Tuesday,
        CrmDayOfWeek.Wednesday,
        CrmDayOfWeek.Thursday,
        CrmDayOfWeek.Friday,
    ]);
    const [dayPreset, setDayPreset] = useState<string>("weekdays");
    const [acceptsSameDay, setAcceptsSameDay] = useState<boolean | null>(null);
    const [sameDayCutoffTime, setSameDayCutoffTime] = useState<string>("12:00");
    const [advanceBookingDays, setAdvanceBookingDays] = useState<number>(2);

    // Update form fields when initialData changes
    useEffect(() => {
        if (app?.availability) {
            setServiceDays(app.availability.availableDays);
            setAcceptsSameDay(app.availability.defaultBusyDays?.blockCurrentDay);
            setSameDayCutoffTime(app.availability.defaultBusyDays?.currentDayAvailableUntil || "12:00");
            setAdvanceBookingDays(app.availability.defaultBusyDays?.blockNextBusinessDays || 2);

            // Determine preset based on initial service days
            const days = app.availability?.availableDays?.sort() || [];
            const weekdays = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY"].sort();
            const sevenDays = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"].sort();
            const weekdaysSaturday = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"].sort();

            if (JSON.stringify(days) === JSON.stringify(weekdays)) {
                setDayPreset("weekdays");
            } else if (JSON.stringify(days) === JSON.stringify(sevenDays)) {
                setDayPreset("seven_days");
            } else if (JSON.stringify(days) === JSON.stringify(weekdaysSaturday)) {
                setDayPreset("weekdays_saturday");
            } else {
                setDayPreset("custom");
            }
        }
    }, [app]);

    const handleDayToggle = useCallback((day: CrmDayOfWeek) => {
        setServiceDays((prev) => (prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]));
    }, []);

    const handleDayPresetChange = useCallback((preset: string) => {
        setDayPreset(preset);
        const selectedPreset = DAY_PRESET_OPTIONS.find((option) => option.value === preset);
        if (selectedPreset && preset !== "custom") {
            setServiceDays(selectedPreset.days);
        }
    }, []);

    const handleContinue = useCallback(() => {
        const availabilityData: Pick<ClientApp, "availability" | "appId" | "organizationId"> = {
            availability: {
                ...app?.availability,
                defaultBusyDays: {
                    ...app?.availability?.defaultBusyDays,
                    blockCurrentDay: acceptsSameDay,
                    currentDayAvailableUntil: acceptsSameDay ? sameDayCutoffTime : null,
                    blockNextBusinessDays: acceptsSameDay ? null : advanceBookingDays,
                    availableDays: serviceDays,
                },
            },
            appId: app?.appId || "",
            organizationId: app?.organizationId || "",
        };

        onNext?.(availabilityData);
    }, [
        app?.availability,
        app?.appId,
        app?.organizationId,
        serviceDays,
        acceptsSameDay,
        sameDayCutoffTime,
        advanceBookingDays,
        onNext,
    ]);

    const isValid = serviceDays?.length > 0 && acceptsSameDay !== null;

    return (
        <>
            {aside && <OnboardingAside>{aside}</OnboardingAside>}

            <OnboardingContent>
                {saving || loading ? (
                    <div
                        style={{
                            padding: "40px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "400px",
                        }}
                    >
                        <LoadingSpinner
                            textStyle={{ fontSize: "1em" }}
                            loadingText={saving ? "Updating availability settings..." : "Loading details..."}
                        />
                    </div>
                ) : (
                    <div style={{ padding: "20px", maxWidth: "600px", margin: "0 auto" }}>
                        {/* Service Days Selection */}
                        <div style={{ marginBottom: "30px" }}>
                            <h3 style={{ marginBottom: "15px" }}>What days of the week do you provide service?</h3>

                            <div style={{ marginBottom: "20px" }}>
                                {DAY_PRESET_OPTIONS.map((option) => (
                                    <div key={option.value} style={{ marginBottom: "10px" }}>
                                        <FieldCheckbox
                                            label={option.label}
                                            value={dayPreset === option.value}
                                            onChange={() => handleDayPresetChange(option.value)}
                                        />
                                    </div>
                                ))}
                            </div>

                            {dayPreset === "custom" && (
                                <Accordion title="Select individual days" expanded={true} disableExpandIcon={true}>
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                                            gap: "10px",
                                            padding: "15px 0",
                                        }}
                                    >
                                        {DAYS_OF_WEEK.map((day) => (
                                            <div key={day.value}>
                                                <FieldCheckbox
                                                    label={day.label}
                                                    value={serviceDays?.includes(day.value) || false}
                                                    onChange={() => handleDayToggle(day.value)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </Accordion>
                            )}
                        </div>

                        {/* Same Day Service */}
                        <div style={{ marginBottom: "30px" }}>
                            <h3 style={{ marginBottom: "15px" }}>Do you accept same-day bookings?</h3>

                            <div style={{ marginBottom: "15px" }}>
                                <Accordion
                                    title={
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <FieldCheckbox
                                                fieldStyle={{ marginBottom: "2px" }}
                                                value={acceptsSameDay === true}
                                                onChange={() => setAcceptsSameDay(true)}
                                            />
                                            <span style={{ marginLeft: "8px" }}>Yes</span>
                                        </div>
                                    }
                                    expanded={acceptsSameDay === true}
                                    disableExpandIcon={true}
                                    onChange={(_, expanded) => setAcceptsSameDay(expanded ? true : null)}
                                >
                                    <div style={{ padding: "15px 0" }}>
                                        <FieldDropdown
                                            label="What's the latest time you accept same-day bookings?"
                                            value={sameDayCutoffTime}
                                            onChange={(value) => setSameDayCutoffTime(value)}
                                            options={TIME_OPTIONS}
                                        />
                                    </div>
                                </Accordion>
                            </div>

                            <div style={{ marginBottom: "15px" }}>
                                <Accordion
                                    title={
                                        <div style={{ display: "flex", alignItems: "center" }}>
                                            <FieldCheckbox
                                                fieldStyle={{ marginBottom: "2px" }}
                                                value={acceptsSameDay === false}
                                                onChange={() => setAcceptsSameDay(false)}
                                            />
                                            <span style={{ marginLeft: "8px" }}>No</span>
                                        </div>
                                    }
                                    expanded={acceptsSameDay === false}
                                    disableExpandIcon={true}
                                    onChange={(_, expanded) => setAcceptsSameDay(expanded ? false : null)}
                                >
                                    <div style={{ padding: "15px 0" }}>
                                        <FieldDropdown
                                            label="How many business days out can customers book?"
                                            value={advanceBookingDays.toString()}
                                            onChange={(value) => setAdvanceBookingDays(Number(value))}
                                            options={ADVANCE_BOOKING_OPTIONS.map((opt) => ({
                                                value: opt.value.toString(),
                                                text: opt.text,
                                            }))}
                                        />
                                    </div>
                                </Accordion>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                marginTop: "40px",
                            }}
                        >
                            {onBack && <Button label="Back" variation="secondary" onClick={onBack} />}
                            <Button
                                label="Continue"
                                disabled={!isValid}
                                onClick={handleContinue}
                                style={{ marginLeft: "auto" }}
                            />
                        </div>
                    </div>
                )}
            </OnboardingContent>
        </>
    );
};
